/*
 * Harta Leaflet a temei ALMAM. Serveste ORICE sectiune care are nevoie de harta
 * (subsolul si pagina de contact), ca sa nu existe doua implementari care se
 * abat una de la alta in timp.
 *
 * De ce Leaflet si nu embed-ul openstreetmap.org sau Google: embed-ul e un
 * iframe de pe alt domeniu, deci butoanele lui de zoom si bara lui de credite
 * nu pot fi stilizate din tema, iar varianta oficiala Google cere cheie si cont
 * cu facturare. Cu Leaflet, harta e in DOM-ul nostru si arata cum cere designul.
 *
 * Initializarea e amanata pana cand harta intra in ecran: in subsol ea apare pe
 * FIECARE pagina a magazinului, iar tile-urile n-au de ce sa se descarce pentru
 * un vizitator care nu ajunge niciodata acolo.
 *
 * Ce citeste din DOM:
 *   data-almam-map              - marcheaza containerul
 *   data-lat / data-lng         - centrul si pozitia pinului (obligatorii)
 *   data-zoom                   - implicit 16
 *   data-tiles / data-attribution
 *   data-label                  - titlul pinului
 *   data-pin-class              - clasa pusa pe pin, ca fiecare sectiune sa-l
 *                                 poata stiliza separat
 *   data-pin-width / -height    - necesare aici, nu in CSS: Leaflet calculeaza
 *                                 `iconAnchor` din ele, ca varful pinului sa
 *                                 cada exact pe coordonate, nu centrul lui
 *   <template data-map-pin>     - optional; forma pinului. Sta in Liquid, nu
 *                                 aici, ca SVG-ul sa poata folosi snippet-urile
 *                                 de iconuri ale temei fara sa fie duplicat in JS
 *
 * Harta cu MAI MULTE puncte (pagina "Magazine"):
 *   data-points                 - JSON: [{ id, lat, lng, label, address }]
 *                                 Cand exista, `data-lat`/`data-lng` devin doar
 *                                 rezerva pentru cazul in care lista e goala,
 *                                 iar incadrarea se face din marginile tuturor
 *                                 punctelor, nu dintr-un centru fix.
 *   data-fit-padding            - marginea lasata la incadrare, implicit 40px
 *
 * Focalizarea din lista: orice element cu `data-map-focus="<id>"` aflat in
 * acelasi `[data-almam-map-scope]` duce harta la punctul cu id-ul acela si ii
 * deschide eticheta. Legatura se face aici, nu in sectiune, fiindca e treaba
 * hartii sa stie cum se ajunge la un punct - sectiunea doar cere.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-almam-map]';

  /* Raportul viewBox-ului picaturii e 24:32, deci inaltimea e latimea inmultita
     cu 4/3. Valorile astea se folosesc doar daca sectiunea nu le trimite. */
  var DEFAULT_PIN_WIDTH = 19;
  var DEFAULT_PIN_HEIGHT = 25;

  var DEFAULT_PIN_SVG =
    '<svg viewBox="0 0 24 32" width="100%" height="100%" aria-hidden="true" focusable="false">' +
    '<path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20c0-6.6-5.4-12-12-12z"/>' +
    '<circle cx="12" cy="12" r="4.4"/>' +
    '</svg>';

  function readNumber(el, name, fallback) {
    var value = parseFloat(el.getAttribute(name));
    return isNaN(value) ? fallback : value;
  }

  function buildPin(el) {
    var width = readNumber(el, 'data-pin-width', DEFAULT_PIN_WIDTH);
    var height = readNumber(el, 'data-pin-height', DEFAULT_PIN_HEIGHT);

    /* Sablonul e cautat langa harta, nu global: doua sectiuni cu harta pe
       aceeasi pagina trebuie sa poata avea pini diferiti. */
    var parent = el.parentNode || document;
    var template = parent.querySelector('[data-map-pin]');
    var html = template ? template.innerHTML : DEFAULT_PIN_SVG;

    return window.L.divIcon({
      className: el.getAttribute('data-pin-class') || 'almam-map__pin',
      html: html,
      iconSize: [width, height],
      iconAnchor: [width / 2, height],
    });
  }

  function readPoints(el) {
    var raw = el.getAttribute('data-points');
    if (!raw) return [];
    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      /* Un JSON stricat nu trebuie sa lase pagina fara harta: se cade inapoi
         pe punctul unic din `data-lat`/`data-lng`. */
      return [];
    }
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(function (point) {
      return point && isFinite(point.lat) && isFinite(point.lng);
    });
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function buildPopup(point) {
    var html = '<strong class="almam-map__popup-title">' + escapeHtml(point.label) + '</strong>';
    if (point.address) {
      html += '<span class="almam-map__popup-address">' + escapeHtml(point.address) + '</span>';
    }
    return '<div class="almam-map__popup">' + html + '</div>';
  }

  var leafletLoading = null;

  function loadLeaflet(el) {
    if (window.L) return Promise.resolve();
    if (leafletLoading) return leafletLoading;

    var jsUrl = el.getAttribute('data-leaflet-js');
    if (!jsUrl) return Promise.reject(new Error('leaflet url missing'));

    var cssUrl = el.getAttribute('data-leaflet-css');
    if (cssUrl) {
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cssUrl;
      /* Inainte de orice alta foaie: suprascrierile temei au aceeasi specificitate. */
      document.head.insertBefore(link, document.head.querySelector('link[rel="stylesheet"]'));
    }

    leafletLoading = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = jsUrl;
      script.onload = resolve;
      script.onerror = function () {
        leafletLoading = null;
        reject(new Error('leaflet failed to load'));
      };
      document.head.appendChild(script);
    });
    return leafletLoading;
  }

  function build(el) {
    if (el.dataset.almamMapReady === 'true') return;
    if (!window.L) {
      loadLeaflet(el).then(
        function () {
          build(el);
        },
        function () {}
      );
      return;
    }
    el.dataset.almamMapReady = 'true';

    var lat = readNumber(el, 'data-lat', null);
    var lng = readNumber(el, 'data-lng', null);
    if (lat === null || lng === null) return;

    var map = window.L.map(el, {
      center: [lat, lng],
      zoom: readNumber(el, 'data-zoom', 16),
      zoomControl: false,
      /* Rotita mouse-ului ramane a paginii. Altfel un vizitator care deruleaza
         prin pagina se blocheaza in harta si nu mai poate iesi. */
      scrollWheelZoom: false,
      attributionControl: true,
    });

    window.L.control.zoom({ position: 'bottomright' }).addTo(map);

    window.L.tileLayer(el.getAttribute('data-tiles'), {
      attribution: el.getAttribute('data-attribution') || '',
      maxZoom: 19,
    }).addTo(map);

    var icon = buildPin(el);
    var points = readPoints(el);

    if (points.length) {
      var markers = {};
      var latlngs = [];

      points.forEach(function (point) {
        var marker = window.L.marker([point.lat, point.lng], {
          icon: icon,
          keyboard: false,
          title: point.label || '',
        }).addTo(map);

        if (point.label) {
          marker.bindPopup(buildPopup(point));
        }

        markers[String(point.id)] = marker;
        latlngs.push([point.lat, point.lng]);
      });

      /* Un singur punct nu are margini din care sa reiasa o incadrare, iar
         `fitBounds` pe el ar duce harta la zoom maxim. */
      if (latlngs.length > 1) {
        map.fitBounds(latlngs, { padding: [readNumber(el, 'data-fit-padding', 40), readNumber(el, 'data-fit-padding', 40)] });
      } else {
        map.setView(latlngs[0], readNumber(el, 'data-zoom', 16));
      }

      el.almamMapPoints = { map: map, markers: markers };
    } else {
      window.L.marker([lat, lng], {
        icon: icon,
        keyboard: false,
        title: el.getAttribute('data-label') || '',
      }).addTo(map);
    }

    /* Creditele sunt obligatorii legal, dar nu trebuie sa strige: bara nu are
       prefixul "Leaflet" si textul e cel minim din setari. */
    map.attributionControl.setPrefix('');
  }

  function observe(el) {
    if (!('IntersectionObserver' in window)) {
      build(el);
      return;
    }
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          build(entry.target);
        });
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
  }

  function init(root) {
    (root || document).querySelectorAll(SELECTOR).forEach(observe);
  }

  /* Focalizarea unui punct din lista.

     Ascultarea se face pe document, nu pe fiecare buton: lista se re-randeaza in
     editorul de teme la fiecare modificare, iar ascultatorii pusi pe butoane ar
     ramane pe noduri care nu mai exista. */
  function focusFromList(event) {
    var trigger = event.target.closest('[data-map-focus]');
    if (!trigger) return;

    var scope = trigger.closest('[data-almam-map-scope]');
    if (!scope) return;

    var el = scope.querySelector(SELECTOR);
    if (!el) return;

    /* Harta se construieste abia cand intra in ecran. Daca vizitatorul apasa un
       card inainte de asta - pe telefon lista e deasupra hartii, deci se poate -
       harta se construieste acum, altfel apasarea n-ar face nimic. */
    if (!el.almamMapPoints) build(el);
    if (!el.almamMapPoints) return;

    var marker = el.almamMapPoints.markers[trigger.getAttribute('data-map-focus')];
    if (!marker) return;

    var map = el.almamMapPoints.map;
    map.invalidateSize();
    map.setView(marker.getLatLng(), Math.max(map.getZoom(), 15), { animate: true });
    marker.openPopup();

    /* Pe telefon harta e sub lista, deci fara asta vizitatorul apasa un card si
       nu vede nimic schimbandu-se. */
    if (window.matchMedia('(max-width: 989px)').matches) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  document.addEventListener('click', focusFromList);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /* In editorul de teme sectiunea se re-randeaza la fiecare modificare, deci
     harta trebuie construita din nou pe nodul nou. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
